import React, { useState } from "react";
import { useLocalStorageSerializeKey } from "../../../lib/localStorage";
import { getContract } from "../../../config/contracts";
import useSWR from "swr";
import { callContract, contractFetcher } from "../../../lib/contracts";
import Token from "../../../abis/Token.json";
import { approveTokens } from "../../../domain/tokens";
import { ethers } from "ethers";
import RewardRouter from "../../../abis/RewardRouter.json";
import classes from "./CompoundModalStyles.module.scss";
import Checkbox from "../../Checkbox/Checkbox";
import { ActionButton } from "../../Buttons/ActionButton/ActionButton";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "../../../lib/chains";
import { t, Trans } from "@lingui/macro";
import { ModalBase } from "../ModalBase/ModalBase";
import { IoWarningOutline } from "react-icons/io5";

type Props = {
  setPendingTxns: any;
  onClose: () => void;
  rewardRouterAddress: string;
  totalVesterRewards: any;
  wrappedTokenSymbol: string;
  nativeTokenSymbol: string;
};

export const CompoundModal = ({
  setPendingTxns,
  onClose,
  rewardRouterAddress,
  totalVesterRewards,
  wrappedTokenSymbol,
  nativeTokenSymbol,
}: Props) => {
  const { active, library, account } = useWeb3React();
  const { chainId } = useChainId();

  const [isCompounding, setIsCompounding] = useState(false);
  const [shouldClaimCad, setShouldClaimCad] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-cad"],
    true
  );
  const [shouldStakeCad, setShouldStakeCad] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-stake-cad"],
    true
  );
  const [shouldClaimEsCad, setShouldClaimEsCad] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-es-cad"],
    true
  );
  const [shouldStakeEsCad, setShouldStakeEsCad] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-stake-es-cad"],
    true
  );
  const [shouldStakeMultiplierPoints, setShouldStakeMultiplierPoints] = useState(true);
  const [shouldClaimWeth, setShouldClaimWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-claim-weth"],
    true
  );
  const [shouldConvertWeth, setShouldConvertWeth] = useLocalStorageSerializeKey(
    [chainId, "StakeV2-compound-should-convert-weth"],
    true
  );

  const cadAddress = getContract(chainId, "CAD");
  const stakedCadTrackerAddress = getContract(chainId, "StakedCadTracker");

  const [isApproving, setIsApproving] = useState(false);

  const { data: tokenAllowance } = useSWR(
    // @ts-ignore
    // TODO: check props of this hook, seems some parameters are wrong type
    active && [active, chainId, cadAddress, "allowance", account, stakedCadTrackerAddress],
    {
      fetcher: contractFetcher(library, Token),
    }
  );

  const needApproval = shouldStakeCad && tokenAllowance && totalVesterRewards && totalVesterRewards.gt(tokenAllowance);

  const isPrimaryEnabled = () => {
    return !isCompounding && !isApproving && !isCompounding;
  };

  const getPrimaryText = () => {
    if (isApproving) {
      return t`Approving CAD...`;
    }
    if (needApproval) {
      return t`Approve CAD`;
    }
    if (isCompounding) {
      return t`Compounding...`;
    }
    return t`Compound`;
  };

  const onClickPrimary = () => {
    if (needApproval) {
      //TODO: this function is missing some props, need to take a look

      // @ts-ignore
      approveTokens({
        setIsApproving,
        library,
        tokenAddress: cadAddress,
        spender: stakedCadTrackerAddress,
        chainId,
      });
      return;
    }

    setIsCompounding(true);

    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, library.getSigner());
    callContract(
      chainId,
      contract,
      "handleRewards",
      [
        shouldClaimCad || shouldStakeCad,
        shouldStakeCad,
        shouldClaimEsCad || shouldStakeEsCad,
        shouldStakeEsCad,
        shouldStakeMultiplierPoints,
        shouldClaimWeth || shouldConvertWeth,
        shouldConvertWeth,
      ],
      {
        sentMsg: t`Compound submitted!`,
        failMsg: t`Compound failed.`,
        successMsg: t`Compound completed!`,
        setPendingTxns,
      }
    )
      .then(async (res) => {
        onClose();
      })
      .finally(() => {
        setIsCompounding(false);
      });
  };

  const toggleShouldStakeCad = (value: boolean) => {
    if (value) {
      setShouldClaimCad(value);
    }
    setShouldStakeCad(value);
  };

  const toggleShouldStakeEsCad = (value: boolean) => {
    if (value) {
      setShouldClaimEsCad(value);
    }
    setShouldStakeEsCad(value);
  };

  const toggleConvertWeth = (value: boolean) => {
    if (value) {
      setShouldClaimWeth(value);
    }
    setShouldConvertWeth(value);
  };

  return (
    <ModalBase onClose={onClose} title={"Compound Rewards"}>
      <div className={classes.wrapper}>
        <div className={classes.row}>
          <div className={classes.column}>
            <span className={classes.columnTitle}>CAD</span>
            <Checkbox
              isChecked={shouldClaimCad}
              className={classes.checkBox}
              setIsChecked={setShouldClaimCad}
              disabled={shouldStakeCad}
            >
              <Trans>Claim Rewards</Trans>
            </Checkbox>
            <Checkbox isChecked={shouldStakeCad} className={classes.checkBox} setIsChecked={toggleShouldStakeCad}>
              <Trans>Stake Rewards</Trans>
            </Checkbox>
          </div>
          <div className={classes.column}>
            <span className={classes.columnTitle}>esCAD</span>
            <Checkbox
              isChecked={shouldClaimEsCad}
              className={classes.checkBox}
              setIsChecked={setShouldClaimEsCad}
              disabled={shouldStakeEsCad}
            >
              <Trans>Claim esCAD Rewards</Trans>
            </Checkbox>
            <Checkbox isChecked={shouldStakeEsCad} className={classes.checkBox} setIsChecked={toggleShouldStakeEsCad}>
              <Trans>Stake esCAD Rewards</Trans>
            </Checkbox>
          </div>
        </div>
        <div className={classes.column}>
          <span className={classes.columnTitle}>WCANTO</span>
          <div className={classes.row}>
            <Checkbox
              isChecked={shouldClaimWeth}
              className={classes.checkBox}
              setIsChecked={setShouldClaimWeth}
              disabled={shouldConvertWeth}
            >
              <Trans>Claim</Trans> {wrappedTokenSymbol} <Trans>Rewards</Trans>
            </Checkbox>
            <Checkbox isChecked={shouldConvertWeth} className={classes.checkBox} setIsChecked={toggleConvertWeth}>
              <Trans>Convert</Trans> {wrappedTokenSymbol} <Trans>to</Trans> {nativeTokenSymbol}
            </Checkbox>
          </div>
        </div>
        <div className={classes.warningWrapper}>
          <IoWarningOutline /> All your Multiplier Points will be staked
        </div>
        <ActionButton
          onClick={onClickPrimary}
          type={"outlined"}
          buttonText={getPrimaryText()}
          disabled={!isPrimaryEnabled()}
          className={classes.button}
        />
      </div>
    </ModalBase>
  );
};
